import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ========== ARTICLES API ==========
export const articlesApi = {
  getAll: (params = {}) => apiClient.get('/articles', { params }),
  getBySlug: (slug) => apiClient.get(`/articles/${slug}`),
  create: (data) => apiClient.post('/articles', data),
  update: (slug, data) => apiClient.put(`/articles/${slug}`, data),
  delete: (slug) => apiClient.delete(`/articles/${slug}`),
  trackView: (slug) => apiClient.post(`/articles/${slug}/view`),
  getPopular: (limit = 10) => apiClient.get('/articles/popular/trending', { params: { limit } }),
  getLatest: (limit = 1) => apiClient.get('/articles/featured').then(r => ({ data: r.data ? [r.data] : [] })).catch(() => apiClient.get('/articles', { params: { published: true, sort: '-created_at', limit } })),
  getFeaturedList: (limit = 3, pinnedOnly = false) =>
    apiClient.get('/articles/featured-list', { params: { limit, pinned_only: pinnedOnly } }),
  search: (q, limit = 20) => apiClient.get('/articles/search', { params: { q, limit } }),
  siteSearch: (q) => apiClient.get('/search', { params: { q } }),
  getRelated: (slug, limit = 3) => apiClient.get(`/articles/${slug}/related`, { params: { limit } }),
  getAiRecommendations: (slug, exclude = '', limit = 4) =>
    apiClient.get(`/articles/${slug}/ai-recommendations`, { params: { exclude, limit } }),
  getSeries: (slug) => apiClient.get(`/articles/${slug}/series`),
};

// ========== ARTICLE FEEDBACK API ==========
export const articleFeedbackApi = {
  submit: (slug, payload) => apiClient.post(`/articles/${slug}/feedback`, payload),
  getStats: (slug) => apiClient.get(`/articles/${slug}/feedback-stats`),
};

// ========== INSIGHTS API ==========
export const insightsApi = {
  strikingDistance: (params = {}) =>
    apiClient.get('/insights/striking-distance', { params }),
  strikingDistanceSummary: () =>
    apiClient.get('/insights/striking-distance/summary'),
};

// ========== PRODUCTS API ==========
export const productsApi = {
  getAll: (params = {}) => apiClient.get('/products', { params }),
  getBySlug: (slug) => apiClient.get(`/products/${slug}`),
  create: (data) => apiClient.post('/products', data),
  update: (slug, data) => apiClient.put(`/products/${slug}`, data),
  delete: (slug) => apiClient.delete(`/products/${slug}`),
};

// ========== CART API ==========
export const cartApi = {
  create: () => apiClient.post('/cart'),
  get: (cartId) => apiClient.get(`/cart/${cartId}`),
  addItem: (cartId, productId, quantity = 1, variant = null) =>
    apiClient.post(`/cart/${cartId}/items`, {
      product_id: productId,
      quantity,
      variant_id: variant?.id ? String(variant.id) : null,
      variant: variant?.title || null,
    }),
  updateItem: (cartId, productId, quantity, variantId = null) =>
    apiClient.put(`/cart/${cartId}/items/${productId}`, { quantity, variant_id: variantId ? String(variantId) : null }),
  removeItem: (cartId, productId, variantId = null) =>
    apiClient.delete(`/cart/${cartId}/items/${productId}`, { params: variantId ? { variant_id: String(variantId) } : {} }),
  associateEmail: (cartId, email) =>
    apiClient.post(`/cart/${cartId}/email`, { email }),
};

// ========== AUTH API ==========
export const authApi = {
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => apiClient.post('/auth/reset-password', { token, password }),
};

// ========== CHECKOUT API ==========
export const directoryApi = {
  getDirectory: (directoryId) => apiClient.get(`/directory/${directoryId}`),
  getDirectoryIndex: () => apiClient.get('/directory/index'),
  subscribeRaceAlerts: (payload) => apiClient.post('/directory/race-alerts', payload),
  submitVideo: (gymId, data) => apiClient.post(`/directory/gyms/${gymId}/video`, data),
  claimListing: (gymId, data) => apiClient.post(`/directory/gyms/${gymId}/claim`, data),
  getGymPreview: (gymId) => apiClient.get(`/directory/gyms/${gymId}/preview`),
  approveGymListing: (gymId, data) => apiClient.post(`/directory/gyms/${gymId}/approve`, data),
  requestListingChanges: (gymId, data) => apiClient.post(`/directory/gyms/${gymId}/request-changes`, data),
};

export const checkoutApi = {
  getConfig: () => apiClient.get('/checkout/config'),
  createSession: (data) => apiClient.post('/checkout/create-session', data),
  getStatus: (sessionId) => apiClient.get(`/checkout/status/${sessionId}`),
  buyNow: (data) => apiClient.post('/checkout/buy-now', data),
  shopifyCartUrl: (data) => apiClient.post('/checkout/shopify-cart-url', data),
  shopifyBuyNowUrl: (data) => apiClient.post('/checkout/shopify-buy-now-url', data),
  getShippingRates: (subtotal) => apiClient.get('/shipping/rates', { params: { subtotal } }),
};

// ========== LEADS API ==========
export const leadsApi = {
  create: (data) => apiClient.post('/leads', data),
  createPeptidePopup: (data) => apiClient.post('/leads/peptide-popup', data),
  sendLongevityReport: (data) => apiClient.post('/leads/longevity-report', data),
  getAll: (params = {}) => apiClient.get('/leads', { params }),
};

// ========== GYM SUBMISSIONS API ==========
export const gymSubmissionsApi = {
  submit: (data) => apiClient.post('/gym-submissions', data),
  getAll: (params = {}) => apiClient.get('/gym-submissions', { params }),
};

// ========== ORDERS API ==========
export const ordersApi = {
  getAll: (params = {}) => apiClient.get('/orders', { params }),
  getById: (orderId) => apiClient.get(`/orders/${orderId}`),
};

// ========== PRODUCT REVIEWS API ==========
export const productReviewsApi = {
  getReviews: (productId) => apiClient.get(`/products/${productId}/reviews`),
  submitReview: (productId, data) => apiClient.post(`/products/${productId}/reviews`, data),
  uploadPhoto: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post('/reviews/photo-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  adminGetAll: () => apiClient.get('/admin/product-reviews'),
  adminUpdate: (reviewId, action) => apiClient.put(`/admin/product-reviews/${reviewId}?action=${action}`),
};

export const alsoBoughtApi = {
  get: (productId) => apiClient.get(`/products/${productId}/also-bought`),
};

// ========== MEAL PLANS API ==========
export const mealPlansApi = {
  getRecipes: () => apiClient.get('/meal-plans/recipes'),
  getDefaultPlan: () => apiClient.get('/meal-plans/default-plan'),
  aiGenerate: (data) => apiClient.post('/meal-plans/ai-generate', data),
  create: (data, token) => apiClient.post('/meal-plans', data, { headers: { Authorization: `Bearer ${token}` } }),
  getAll: (token) => apiClient.get('/meal-plans', { headers: { Authorization: `Bearer ${token}` } }),
  getById: (id, token) => apiClient.get(`/meal-plans/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  update: (id, data, token) => apiClient.put(`/meal-plans/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  delete: (id, token) => apiClient.delete(`/meal-plans/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  getShared: (shareId) => apiClient.get(`/meal-plans/shared/${shareId}`),
};

// ========== AFFILIATE PROGRAM API ==========
export const affiliateApi = {
  validateCode: (code) => apiClient.get(`/affiliate-program/validate/${code}`),
  login: (data) => apiClient.post('/affiliate-program/login', data),
  getDashboard: (code, token) => apiClient.get(`/affiliate-program/dashboard/${code}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
  adminGetAll: (token) => apiClient.get('/affiliate-program/admin/all', { headers: { Authorization: `Bearer ${token}` } }),
  adminUpdateTier: (id, data, token) => apiClient.put(`/affiliate-program/admin/${id}/tier`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminToggle: (id, token) => apiClient.put(`/affiliate-program/admin/${id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  adminPayout: (id, data, token) => apiClient.put(`/affiliate-program/admin/${id}/payout`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminStats: (token) => apiClient.get('/affiliate-program/admin/stats', { headers: { Authorization: `Bearer ${token}` } }),
  getLeaderboard: () => apiClient.get('/affiliate-program/leaderboard'),
};

// ========== MACRO TRACKING API ==========
export const macrosApi = {
  log: (data, token) => apiClient.post('/macros/log', data, { headers: { Authorization: `Bearer ${token}` } }),
  getLogs: (date, token) => apiClient.get(`/macros/logs?date=${date}`, { headers: { Authorization: `Bearer ${token}` } }),
  deleteLog: (id, token) => apiClient.delete(`/macros/log/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  getTargets: (token) => apiClient.get('/macros/targets', { headers: { Authorization: `Bearer ${token}` } }),
  setTargets: (data, token) => apiClient.put('/macros/targets', data, { headers: { Authorization: `Bearer ${token}` } }),
  getSummary: (range, token) => apiClient.get(`/macros/summary?range=${range}`, { headers: { Authorization: `Bearer ${token}` } }),
  getCompliance: (date, token) => apiClient.get(`/macros/compliance?date=${date}`, { headers: { Authorization: `Bearer ${token}` } }),
};

// ========== COMMUNITY API ==========
export const communityApi = {
  getMyProfile: (token) => apiClient.get('/community/profile/me', { headers: { Authorization: `Bearer ${token}` } }),
  updateProfile: (data, token) => apiClient.put('/community/profile/me', data, { headers: { Authorization: `Bearer ${token}` } }),
  getProfile: (username, token) => apiClient.get(`/community/profile/${username}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  follow: (username, token) => apiClient.post(`/community/follow/${username}`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  unfollow: (username, token) => apiClient.delete(`/community/follow/${username}`, { headers: { Authorization: `Bearer ${token}` } }),
  getFollowers: (username, skip = 0) => apiClient.get(`/community/followers/${username}?skip=${skip}`),
  getFollowing: (username, skip = 0) => apiClient.get(`/community/following/${username}?skip=${skip}`),
  getSuggestions: (token) => apiClient.get('/community/suggestions', { headers: { Authorization: `Bearer ${token}` } }),
  getFeed: (token, skip = 0) => apiClient.get(`/community/feed?skip=${skip}`, { headers: { Authorization: `Bearer ${token}` } }),
  getPublicFeed: (skip = 0) => apiClient.get(`/community/feed/public?skip=${skip}`),
  getUserFeed: (username, token, skip = 0) => apiClient.get(`/community/feed/${username}?skip=${skip}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  postActivity: (data, token) => apiClient.post('/community/activity', data, { headers: { Authorization: `Bearer ${token}` } }),
  discover: () => apiClient.get('/community/discover'),
  toggleLike: (activityId, token) => apiClient.post(`/community/activity/${activityId}/like`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  getComments: (activityId) => apiClient.get(`/community/activity/${activityId}/comments`),
  addComment: (activityId, content, token) => apiClient.post(`/community/activity/${activityId}/comment`, { content }, { headers: { Authorization: `Bearer ${token}` } }),
  deleteComment: (commentId, token) => apiClient.delete(`/community/comment/${commentId}`, { headers: { Authorization: `Bearer ${token}` } }),
  uploadImage: (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/community/upload-image', formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
    });
  },
  getImageUrl: (path) => `${API}/community/image/${path}`,
};

// ========== CHALLENGES API ==========
export const challengesApi = {
  list: (status = 'active', token) => apiClient.get(`/community/challenges?status=${status}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  get: (id, token) => apiClient.get(`/community/challenges/${id}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  create: (data, token) => apiClient.post('/community/challenges', data, { headers: { Authorization: `Bearer ${token}` } }),
  join: (id, token) => apiClient.post(`/community/challenges/${id}/join`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  log: (id, data, token) => apiClient.post(`/community/challenges/${id}/log`, data, { headers: { Authorization: `Bearer ${token}` } }),
  leaderboard: (id) => apiClient.get(`/community/challenges/${id}/leaderboard`),
  delete: (id, token) => apiClient.delete(`/community/challenges/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
};

// ========== REWARDS API ==========
export const rewardsApi = {
  getMyRewards: (token) => apiClient.get('/rewards/me', { headers: { Authorization: `Bearer ${token}` } }),
  getTiers: () => apiClient.get('/rewards/tiers'),
  getBadges: () => apiClient.get('/rewards/badges'),
  getHistory: (token, skip = 0) => apiClient.get(`/rewards/history?skip=${skip}`, { headers: { Authorization: `Bearer ${token}` } }),
  redeem: (points, token) => apiClient.post('/rewards/redeem', { points }, { headers: { Authorization: `Bearer ${token}` } }),
  dailyLogin: (token) => apiClient.post('/rewards/daily-login', {}, { headers: { Authorization: `Bearer ${token}` } }),
  getLeaderboard: () => apiClient.get('/rewards/leaderboard'),
  getLastOrderPoints: (token) => apiClient.get('/rewards/last-order-points', { headers: { Authorization: `Bearer ${token}` } }),
};

// ========== MACROS EXTRA ==========
export const macrosExtraApi = {
  getStreak: (token) => apiClient.get('/macros/streak', { headers: { Authorization: `Bearer ${token}` } }),
  getWeeklySummary: (token) => apiClient.get('/macros/weekly-summary', { headers: { Authorization: `Bearer ${token}` } }),
};

// ========== SMS API ==========
export const smsApi = {
  getSubscribers: (token) => apiClient.get('/sms/subscribers', { headers: { Authorization: `Bearer ${token}` } }),
  sendBlast: (data, token) => apiClient.post('/sms/blast', data, { headers: { Authorization: `Bearer ${token}` } }),
  getBlasts: (token) => apiClient.get('/sms/blasts', { headers: { Authorization: `Bearer ${token}` } }),
  getStats: (token) => apiClient.get('/sms/stats', { headers: { Authorization: `Bearer ${token}` } }),
  schedule: (data, token) => apiClient.post('/sms/schedule', data, { headers: { Authorization: `Bearer ${token}` } }),
  getScheduled: (token) => apiClient.get('/sms/scheduled', { headers: { Authorization: `Bearer ${token}` } }),
  cancelScheduled: (id, token) => apiClient.delete(`/sms/scheduled/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  triggerNewProduct: (data, token) => apiClient.post('/sms/trigger/new-product', data, { headers: { Authorization: `Bearer ${token}` } }),
  triggerFlashSale: (data, token) => apiClient.post('/sms/trigger/flash-sale', data, { headers: { Authorization: `Bearer ${token}` } }),
};

// ========== CAMPAIGNS API ==========
export const campaignsApi = {
  importServerCsvs: (token) => apiClient.post('/campaigns/import-server-csvs', {}, { headers: { Authorization: `Bearer ${token}` } }),
  getStats: (token) => apiClient.get('/campaigns/stats', { headers: { Authorization: `Bearer ${token}` } }),
  getLeads: (token, tag) => apiClient.get(`/campaigns/leads?tag=${tag}`, { headers: { Authorization: `Bearer ${token}` } }),
  sendPeptiq: (data, token) => apiClient.post('/campaigns/send-peptiq', data, { headers: { Authorization: `Bearer ${token}` } }),
};

// ========== LONGEVITY TRACKING API ==========
export const longevityApi = {
  saveScore: (data, token) => apiClient.post('/longevity/score', data, { headers: { Authorization: `Bearer ${token}` } }),
  getHistory: (token) => apiClient.get('/longevity/history', { headers: { Authorization: `Bearer ${token}` } }),
};

// ========== HEALTH API ==========
export const healthApi = {
  check: () => apiClient.get('/health'),
};

export default apiClient;
